import moment from "moment";
import { formatNights, formatTime, formatTimeDifference, getNumberOfNights, timeSeparator, type TimeDifferenceStrings } from "./momentutils";

export interface ShowDates {
  startDisplayDate: moment.Moment
  endDisplayDate: moment.Moment
  timeOfPerformance: string

  mode: server.dto.AppointmentTimeModes
}

interface RealStartAndEndDate {
  startRealDate: moment.Moment
  endRealDate: moment.Moment
};

// 2026-07-16T01:00:00.000Z" => Thu - Fri
export const inTheNightOfDayName = (startDisplay: moment.Moment) => {
  const shortStartDisplayDayName = startDisplay.format("ddd");
  const shortEndDisplayDayName = startDisplay.add(1, "day").format("ddd");

  return `${shortStartDisplayDayName}-${shortEndDisplayDayName}`
}


export const getRealStartEndDate = (dates: ShowDates): RealStartAndEndDate => {
  if (dates.mode === "allDay") {
    return {
      startRealDate: dates.startDisplayDate,
      endRealDate: dates.endDisplayDate.clone().add(1, "day")
    };
  } else if (dates.mode === "hasTime") {
    return {
      startRealDate: dates.startDisplayDate,
      endRealDate: dates.endDisplayDate
    };
  } else if (dates.mode === "hasTimeStartsInNight") {
    return {
      startRealDate: dates.startDisplayDate.clone().add(1, "day"),
      endRealDate: dates.endDisplayDate.clone().add(1, "day")
    };
  }

  // `satisfies never` gives compile erorr 
  throw new Error(`Unhandled case: ${dates.mode satisfies never}`)
};

// E.g. "1 hour" or "2 days, 1 night" or empty string.
export const getShowTimeDifferenceLabel = (dates: ShowDates, strings: TimeDifferenceStrings): string => {
  const { startRealDate, endRealDate } = getRealStartEndDate(dates);

  if (startRealDate.isSameOrAfter(endRealDate)) {
    return "";
  }

  let timeDifferenceLabel = formatTimeDifference(startRealDate, endRealDate, strings);
  if (dates.mode === "allDay" && getNumberOfNights(dates.startDisplayDate, dates.endDisplayDate) > 0) {
    timeDifferenceLabel += `, ${formatNights(dates.startDisplayDate, dates.endDisplayDate, strings)}`;
  }

  return timeDifferenceLabel;
};

export const formatTimeOfPerformance = (startDisplay: moment.Moment, endDisplay: moment.Moment) => {
  return `${formatTime(startDisplay)} - ${formatTime(endDisplay)}`;
};

export const isEarlyShow = (startDate: moment.Moment, maxInNightOfHour: number) => startDate.hours() < maxInNightOfHour;

interface TimeOfPerformanceReturnType {
  displayStart: moment.Moment
  displayEnd: moment.Moment
  mode: server.dto.AppointmentTimeModes
};

export const parseTimeOfPerformance = (dates: ShowDates, maxInNightOfHour: number): TimeOfPerformanceReturnType => {
  const timeOfPerformance = dates.timeOfPerformance || "";
  const firstRegex = timeOfPerformance.match(/\d{1,2}[:.[a-zA-Z]]?\d{0,2}\s*[pPaA]?/g)?.slice(0, 2);

  if (!firstRegex) {
    return {
      displayStart: dates.startDisplayDate.clone().hours(10).minutes(0),
      displayEnd: dates.endDisplayDate.clone().hours(11).minutes(0),
      mode: "hasTime"
    };
  }

  const result: Array<{
    timeString: string
    hours: number
    minutes: number
  }> = [];
  for (let i = 0; i < firstRegex.length; i++) {
    const d = new Date();
    const time = firstRegex[i].match(/(\d{1,2})[:.[a-zA-Z]]?(\d\d)?\s*([pPaA]?)/);

    if (!time) {
      continue;
    }
    if (time[3]) {
      if ((time[3].toLowerCase() === "a" && parseInt(time[1], 10) === 12)) {
        d.setHours(parseInt(time[1], 10) - 12);
      } else if (time[3].toLowerCase() === "p") {
        d.setHours(parseInt(time[1], 10) + 12);
      } else {
        d.setHours(parseInt(time[1], 10));
      }
    } else {
      d.setHours(parseInt(time[1], 10));
    }

    d.setMinutes(parseInt(time[2], 10) || 0);

    result[i] = {
      timeString: ("00" + d.getHours()).slice(-2) + timeSeparator() + ("00" + d.getMinutes()).slice(-2),
      hours: d.getHours(),
      minutes: d.getMinutes()
    };
  }
  const startHour = result[0]?.hours;
  const startMinute = result[0]?.minutes;
  const endHour = result[1]?.hours;
  const endMinute = result[1]?.minutes;

  const isSingleMoment = startHour != null && startMinute != null && endHour == null && endMinute == null;
  const invalidDate = startHour == null && startMinute == null && endHour == null && endMinute == null;

  let displayStart = dates.startDisplayDate;
  let displayEnd = dates.endDisplayDate;

  if (invalidDate) {
    displayStart = displayStart.clone().hours(10).minutes(0);
    displayEnd = displayEnd.clone().hours(11).minutes(0);
  } else if (isSingleMoment) {
    displayStart = displayStart.clone().hours(startHour).minutes(startMinute);
    displayEnd = displayEnd.clone().hours(startHour).minutes(startMinute);
  } else {
    displayStart = displayStart.clone().hours(startHour ?? 10).minutes(startMinute ?? 0);
    displayEnd = displayEnd.clone().hours(endHour ?? 11).minutes(endMinute ?? 0);

    if (displayEnd.isBefore(displayStart)) {
      displayEnd = displayEnd.add(1, "day");
    }
  }

  return {
    displayStart,
    displayEnd,
    mode: (isEarlyShow(displayStart, maxInNightOfHour) ? "hasTimeStartsInNight" : "hasTime")
  };
};
